import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { TooltipProvider } from "./components/ui/tooltip";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import DashboardPage from "./pages/DashboardPage";

// iter146 — Route gate: SimpleDashboard for members who opt-in.
// iter163 — Default is ADVANCED. New users see a one-time "Pick your dashboard"
// picker modal on first login (handled inside DashboardPage). Existing users
// who explicitly set `simple_dashboard: true` continue to see Simple.
// iter186 — Soft-deprecated. The `user.simple_dashboard` flag is now ignored.
// Everyone gets DashboardPage. Simple files (SimpleDashboard.jsx,
// SimpleGroupPage.jsx, DashboardModePicker.jsx) are kept temporarily for
// 1-week revert safety; will be deleted in the next cleanup pass.
function HomeRoute() {
  return <DashboardPage />;
}
import GroupsPage from "./pages/GroupsPage";
import GroupDetailPage from "./pages/GroupDetailPage";
import TripSummaryPage from "./pages/TripSummaryPage";

// iter186 — GroupRoute also forced to dense view. See HomeRoute comment.
function GroupRoute() {
  return <GroupDetailPage />;
}
import SessionDetailPage from "./pages/SessionDetailPage";
import SettleLandingPage from "./pages/SettleLandingPage";
import ReconcilePage from "./pages/ReconcilePage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import ProfilePage from "./pages/ProfilePage";
import JoinGroupPage from "./pages/JoinGroupPage";
import AdminPage from "./pages/AdminPage";
import NeoPayRestorePage from "./pages/NeoPayRestorePage";
import NeoPaySettingsPage from "./pages/NeoPaySettingsPage";
import NeoPayProofPage from "./pages/NeoPayProofPage";
import NeoPayAssurancePage from "./pages/NeoPayAssurancePage";
import GuestRsvpPage from "./pages/GuestRsvpPage";
import GuestTripPage from "./pages/GuestTripPage";
import GuestCleanupPage from "./pages/GuestCleanupPage";
import FundRepairPage from "./pages/FundRepairPage";
import LedgerSanityPage from "./pages/LedgerSanityPage";
import WhatsAppSettingsPage from "./pages/WhatsAppSettingsPage";
import BackupPage from "./pages/BackupPage";
import AuditLogPage from "./pages/AuditLogPage";
import ErrorLogPage from "./pages/ErrorLogPage";
import PlatformAnalyticsPage from "./pages/PlatformAnalyticsPage";
// iter218 — Internal Wallet (retired iter233 — admin console kept for legacy data)
import AdminWalletPage from "./pages/AdminWalletPage";
import AdminGroupsPage from "./pages/AdminGroupsPage";
import CashRequestsPage from "./pages/CashRequestsPage";
import LandingPage from "./pages/LandingPage";
import ErrorBoundary from "./components/ErrorBoundary";
import OfflineIndicator from "./components/OfflineIndicator";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import EnableFaceIDPrompt from "./components/EnableFaceIDPrompt";
import UpiOnboardingPrompt from "./components/UpiOnboardingPrompt";
import { RealtimeProvider } from "./context/RealtimeContext";
import { BrandingProvider } from "./context/BrandingContext";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary font-display text-2xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    // Logged-out users hitting /, /groups, etc. land on the public welcome page
    return <Navigate to="/welcome" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary font-display text-2xl">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/welcome" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="/join/:code" element={<JoinGroupPage />} />
      {/* iter125 — scan landing route: public so native-camera scans work even
          when the recipient isn't logged in. Page itself shows login CTA. */}
      <Route path="/s/:encoded" element={<SettleLandingPage />} />
      {/* iter197 — Public guest magic links (no auth required) */}
      <Route path="/guest/rsvp/:token" element={<GuestRsvpPage />} />
      <Route path="/guest/trip/:token" element={<GuestTripPage />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<HomeRoute />} />
        <Route path="groups" element={<GroupsPage />} />
        <Route path="groups/:groupId" element={<GroupRoute />} />
        <Route path="groups/:groupId/sessions/:sessionId" element={<SessionDetailPage />} />
        <Route path="groups/:groupId/reconcile" element={<ReconcilePage />} />
        <Route path="groups/:groupId/analytics" element={<AnalyticsPage />} />
        <Route path="groups/:groupId/trip-summary" element={<TripSummaryPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="cash-requests" element={<CashRequestsPage />} />
        <Route path="admin/wallets" element={<AdminWalletPage />} />
        <Route path="admin/groups" element={<AdminGroupsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="admin/analytics" element={<PlatformAnalyticsPage />} />
        <Route path="admin/neopay-restore" element={<NeoPayRestorePage />} />
        <Route path="admin/neopay-settings" element={<NeoPaySettingsPage />} />
        <Route path="admin/neopay-assurance" element={<NeoPayAssurancePage />} />
        <Route path="admin/guest-cleanup" element={<GuestCleanupPage />} />
        <Route path="proof/:transferId" element={<NeoPayProofPage />} />
        <Route path="proof/:source/:id" element={<NeoPayProofPage />} />
        <Route path="admin/fund-repair" element={<FundRepairPage />} />
        <Route path="admin/ledger-sanity" element={<LedgerSanityPage />} />
        <Route path="whatsapp-settings" element={<WhatsAppSettingsPage />} />
        <Route path="backups" element={<BackupPage />} />
        <Route path="audit-log" element={<AuditLogPage />} />
        <Route path="error-logs" element={<ErrorLogPage />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrandingProvider>
          <OfflineIndicator />
          <AuthProvider>
            <BrowserRouter>
              <RealtimeProvider>
                <TooltipProvider delayDuration={300}>
                  <div className="noise-overlay">
                    <AppRoutes />
                    <PWAInstallPrompt />
                    <EnableFaceIDPrompt />
                    <UpiOnboardingPrompt />
                    <Toaster 
                      position="top-center" 
                      richColors 
                      closeButton
                      toastOptions={{
                        className: 'font-body',
                      }}
                    />
                  </div>
                </TooltipProvider>
              </RealtimeProvider>
            </BrowserRouter>
          </AuthProvider>
        </BrandingProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
